import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import Table from "@components/Table";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { ActionsType } from "src/interfaces/Common";
import { MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import useSWRMutation from "swr/mutation";
import AnimationFadeIn from "@components/AnimationFadeIn";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ModalCst from "@components/ModalCst";
import { Grid, TextField, Typography, useTheme, Box } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import EuroIcon from "@mui/icons-material/Euro";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";

interface TableGoodsReceiptsProps {
  stats: any;
  mutateBook: any;
}

const TableGoodsReceipts: React.FC<TableGoodsReceiptsProps> = ({
  stats,
  mutateBook,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["accounting-page"]);
  const [idReceiptRejected, setIdReceiptRejected] = useState<string>();
  const [idCost, setIdCost] = useState<string>();
  const [openModalReject, setOpenModalReject] = useState(false);
  const [costApproved, setCostApproved] = useState<{}>();
  const [openModalApproved, setOpenModalApproved] = useState(false);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["accounting.goods-receipts"]}`;

  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(
      `${ServicesURL.getGoodsReceiptsAll}`,
      PagesID["accounting.goods-receipts"],
      filters[filterId],
    );

  const { trigger: triggerReceiptRejected } = useSWRMutation(
    `${ServicesURL.postGoodsReceipts.replace(":costId", `${idCost}`)}/${idReceiptRejected}/actions/reject`,
    (url: string) => {
      return fetcher<any>(url, MethodType.POST);
    },
  );

  const handleReject = React.useCallback(async () => {
    if (idReceiptRejected) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerReceiptRejected();
        const newData = [
          ...dataTable.output.filter((el: any) => el.id !== idReceiptRejected),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        mutateBook && mutateBook();
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setIdReceiptRejected(undefined);
        setIdCost(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [
    dataTable,
    idReceiptRejected,
    mutate,
    mutateBook,
    triggerReceiptRejected,
  ]);

  const dataFormat = useMemo(() => {
    if (!dataTable?.output) return [];

    return dataTable.output.map((el: any) => {
      el.accounting_type = "RECEIPT";

      if (el.cost) {
        el.cost_label = el.cost.code;
      }

      return el;
    });
  }, [dataTable?.output]);

  const filteredColumns = useMemo(() => {
    if (columns) {
      return columns.map((el) => {
        return { ...el };
      });
    }
  }, [columns]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "button": {
          switch (action.key) {
            case "approve-accounting":
              return {
                ...action,
                getIcon: (data: any) => (
                  <ThumbUpOffAltIcon
                    sx={{
                      fill:
                        data.status === "PENDING_APPROVAL"
                          ? "#1B1B1B !important"
                          : "",
                      backgroundColor:
                        data.status === "PENDING_APPROVAL"
                          ? "#f43e63 !important"
                          : "",
                      padding: data.status === "PENDING_APPROVAL" ? "4px" : "",
                      borderRadius:
                        data.status === "PENDING_APPROVAL" ? "4px" : "",
                    }}
                  />
                ),
                callBack: (data: any) => {
                  setCostApproved(data);
                  setOpenModalApproved(true);
                },
              };
            case "reject-accounting":
              return {
                ...action,
                getIcon: (data: any) => (
                  <ThumbDownOffAltIcon
                    sx={{
                      fill:
                        data.status === "PENDING_APPROVAL"
                          ? "#f43e63 !important"
                          : "",
                      border:
                        data.status === "PENDING_APPROVAL"
                          ? "2px solid #F43E63"
                          : "",
                      padding: data.status === "PENDING_APPROVAL" ? "4px" : "",
                      borderRadius:
                        data.status === "PENDING_APPROVAL" ? "4px" : "",
                    }}
                  />
                ),
                callBack: (data: any) => {
                  setIdReceiptRejected(data.id);
                  setIdCost(data.cost.id);
                  setOpenModalReject(true);
                },
              };
            default:
              return action;
          }
        }
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    configRowNew = configRowNew.map((el: ActionsType) => {
      // RECEIPT: Approve/Reject only if status is PENDING_APPROVAL
      if (
        (el.key === "approve-accounting" || el.key === "reject-accounting") &&
        row.original?.status !== "PENDING_APPROVAL"
      ) {
        return { ...el, disabled: true };
      }

      return { ...el };
    });

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar?.map((action: ActionsType) => {
      return action;
    });
  }, [actionsTopToolbar]);

  const renderCostField = (
    label: string,
    count: number | string = 0,
    value: number | string = 0,
  ) => (
    <Grid item xs={12} sm={3}>
      <Typography sx={{ mb: 1.5, fontWeight: "bold" }}>{label}</Typography>
      <TextField
        type="text"
        size="small"
        value={new Intl.NumberFormat("it-IT", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(value))}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0, pr: 0 }}>
              <Box sx={{ mr: 1 }}>
                <b>{count} -</b>
              </Box>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <EuroIcon />
            </InputAdornment>
          ),
        }}
        variant="outlined"
        fullWidth
        inputProps={{
          style: { paddingLeft: 0 },
        }}
      />
    </Grid>
  );

  const costFields = [
    {
      label: t("gr.total"),
      count: stats?.total_count,
      value: stats?.total_value,
    },
    {
      label: t("gr.pending_approval"),
      count: stats?.pending_approval_count,
      value: stats?.pending_approval_value,
    },
    {
      label: t("gr.approved"),
      count: stats?.approved_count,
      value: stats?.approved_value,
    },
    {
      label: t("gr.rejected"),
      count: stats?.rejected_count,
      value: stats?.rejected_value,
    },
  ];

  return (
    <AnimationFadeIn>
      <Grid container>
        <Grid
          container
          direction="column"
          sx={{
            backgroundColor: theme.palette.background.paper,
            padding: 3,
            width: "100%",
            borderRadius: 1,
            mb: 1,
          }}
        >
          <Grid container spacing={3} sx={{ mb: 1 }}>
            {costFields.map(({ label, count, value }) =>
              renderCostField(label, count, value),
            )}
          </Grid>
        </Grid>
      </Grid>
      <Table
        id={PagesID.accounting}
        data={dataFormat || []}
        columns={filteredColumns || []}
        configurationTopToolbar={actionsToolbar}
        renderRowActions={customRenderRowActions}
        configurationActionForRow={configurationActionForRow}
        numberOfActions={configurationActionForRow?.length}
        totalPages={dataTable?.metadata?.total_elements}
        modalTitle={t("hero-title")?.replace("-", "") || ""}
      />
      <ModalCst
        open={openModalApproved}
        setOpen={setOpenModalApproved}
        typeOfModal={"ManageAccounting"}
        onSubmit={() => {
          setCostApproved(undefined);
          setOpenModalApproved(false);
          mutate && mutate();
          mutateBook && mutateBook();
        }}
        data={costApproved}
      />
      <ModalConfirmsCst
        open={!!openModalReject}
        title={t("reject-accounting", { ns: "modal" })}
        description={t("confirm-reject-accounting", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenModalReject(!openModalReject);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleReject()}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default TableGoodsReceipts;
